import { forwardRef } from 'react'
import type { DayButtonProps } from 'react-day-picker'

import type { RenderDay } from './DateRangeCalendar'

type Props = DayButtonProps & {
  renderDay: RenderDay
}

// `react-day-picker` v9 attaches a ref to `DayButton` to drive keyboard
// focus between cells, so the wrapper must forward it through to the
// rendered button. `type="button"` comes after the spread so the locally
// declared attribute can't be silently overridden by upstream props.
//
// rdp v9 passes `disabled` (HTML) when a day is non-focused-disabled and
// `aria-disabled` when it's the focused day. We always swap the HTML
// disabled for aria-disabled so the host button keeps receiving pointer
// events (Chrome cancels mousedown / click on truly-disabled buttons,
// which prevented per-cell handlers — e.g. the booking calendar's
// "X night minimum stay" popover trigger — from firing). rdp's own
// `handleDayClick` short-circuits on `modifier.disabled`, so selection
// stays blocked.
const DayCell = forwardRef<HTMLButtonElement, Props>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ day, modifiers, renderDay, children, disabled, ...buttonProps }, ref) => (
    <button
      {...buttonProps}
      type="button"
      aria-disabled={modifiers.disabled || undefined}
      ref={ref}
    >
      {renderDay(day.date, modifiers) ?? children}
    </button>
  ),
)

DayCell.displayName = 'DayCell'

export default DayCell
